/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { appPalette } from '@sidekick/design-tokens';
import { useState, type FormEvent } from 'react';
import { EditableMinutes, EditableText } from '../Editable.js';
import { inputStyle, monoLabel } from '../../lib/ui.js';
import { trpc } from '../../lib/trpc.js';

const p = appPalette;

export function OnTheDesk() {
  const utils = trpc.useUtils();
  const list = trpc.actions.list.useQuery();
  const create = trpc.actions.create.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });
  const complete = trpc.actions.complete.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });
  const drop = trpc.actions.drop.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });
  const update = trpc.actions.update.useMutation({
    onSuccess: async () => {
      await utils.actions.list.invalidate();
    },
  });

  const [draft, setDraft] = useState('');
  const [minutes, setMinutes] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const description = draft.trim();
    if (!description) return;
    const parsedMinutes = minutes.trim() === '' ? null : Number.parseInt(minutes.trim(), 10);
    if (parsedMinutes !== null && (!Number.isFinite(parsedMinutes) || parsedMinutes <= 0)) {
      return;
    }
    create.mutate(
      { description, estimatedMinutes: parsedMinutes },
      {
        onSuccess: () => {
          setDraft('');
          setMinutes('');
        },
      },
    );
  };

  // Filter to next-actions only; waiting-for items render in their own
  // region (`<WaitingOn />`) so the desk stays "what I will do today"
  // rather than "things I'm tracking other people on".
  const actions = (list.data ?? []).filter((a) => a.status === 'next');

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ ...monoLabel }}>On the desk · next actions</div>

      {list.isPending ? (
        <span style={{ ...monoLabel, marginTop: 8 }}>…</span>
      ) : actions.length === 0 ? (
        <p
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontStyle: 'italic',
            fontSize: 15,
            color: p.muted,
            margin: 0,
          }}
        >
          The desk is clear.
        </p>
      ) : (
        <div>
          {actions.map((a) => (
            <ActionRow
              key={a.id}
              description={a.description}
              estimatedMinutes={a.estimatedMinutes}
              onComplete={() => complete.mutate({ actionId: a.id })}
              onDrop={() => drop.mutate({ actionId: a.id })}
              onEditDescription={(next) => update.mutate({ actionId: a.id, description: next })}
              onEditMinutes={(next) => update.mutate({ actionId: a.id, estimatedMinutes: next })}
              pending={complete.isPending || drop.isPending || update.isPending}
            />
          ))}
        </div>
      )}

      <form
        onSubmit={onSubmit}
        style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}
      >
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Add a next action"
          aria-label="Add a next action"
          style={{ ...inputStyle, flex: 1 }}
        />
        <input
          type="number"
          value={minutes}
          onChange={(e) => setMinutes(e.target.value)}
          placeholder="min"
          aria-label="Estimated minutes"
          min={1}
          max={600}
          style={{ ...inputStyle, width: 80 }}
        />
        <button
          type="submit"
          disabled={create.isPending || draft.trim() === ''}
          style={{
            padding: '10px 18px',
            background: p.fg,
            color: p.bg,
            border: 'none',
            fontSize: 13,
            cursor: create.isPending || draft.trim() === '' ? 'not-allowed' : 'pointer',
            opacity: create.isPending || draft.trim() === '' ? 0.6 : 1,
          }}
        >
          {create.isPending ? 'Adding…' : 'Add →'}
        </button>
      </form>
      {create.error ? (
        <span style={{ ...monoLabel, color: p.accent }}>{create.error.message}</span>
      ) : null}
    </section>
  );
}

function ActionRow({
  description,
  estimatedMinutes,
  onComplete,
  onDrop,
  onEditDescription,
  onEditMinutes,
  pending,
}: {
  description: string;
  estimatedMinutes: number | null;
  onComplete: () => void;
  onDrop: () => void;
  onEditDescription: (next: string) => void;
  onEditMinutes: (next: number | null) => void;
  pending: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto auto',
        alignItems: 'center',
        gap: 12,
        padding: '12px 0',
        borderTop: `1px solid ${p.line}`,
      }}
    >
      <button
        type="button"
        onClick={onComplete}
        disabled={pending}
        title="Mark complete"
        aria-label="Mark complete"
        style={{
          width: 18,
          height: 18,
          border: `1px solid ${p.line}`,
          background: 'transparent',
          cursor: pending ? 'wait' : 'pointer',
        }}
      />
      <EditableText
        value={description}
        onCommit={(next) => {
          const trimmed = next.trim();
          // Empty after trim or unchanged → silently revert; the server
          // would reject empty anyway.
          if (trimmed === '' || trimmed === description) return;
          onEditDescription(trimmed);
        }}
        ariaLabel="Edit action description"
        renderStyle={{ fontSize: 15, color: p.fg }}
        inputStyle={{ ...inputStyle, fontSize: 15, padding: '6px 8px' }}
      />
      <EditableMinutes
        value={estimatedMinutes}
        onCommit={(next) => {
          if (next === estimatedMinutes) return;
          onEditMinutes(next);
        }}
      />
      <button
        type="button"
        onClick={onDrop}
        disabled={pending}
        title="Drop"
        aria-label="Drop"
        style={{
          background: 'transparent',
          border: 'none',
          color: p.muted,
          cursor: pending ? 'wait' : 'pointer',
          fontSize: 16,
          padding: '0 4px',
        }}
      >
        ×
      </button>
    </div>
  );
}
